// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * What the GNSS hardware was doing when a fix arrived, and the telemetry that
 * makes it answerable at the fleet level.
 *
 * The app cannot turn SBAS/EGNOS or L5 on — no public API exists for either — so
 * the useful move is to measure whether they are in play and tag accuracy with
 * that. Without this, "how accurate is Goffy?" pools a dual-frequency flagship
 * and a single-frequency budget phone into one meaningless average; with it, the
 * question splits into the two populations that actually behave differently, and
 * a claimed improvement can be checked rather than assumed.
 *
 * Everything degrades to "unknown" off Android and in any build without the
 * native module. Unknown is a first-class answer here and never reported as a
 * problem with the fix.
 */

import {
  getGnssSnapshot,
  isGnssStatusAvailable,
  subscribeGnssStatus,
  type GnssSnapshot,
} from '@/modules/gnss-status';

import { incrementCounter, recordHistogram, type MetricTags } from './appMetrics';

/** A snapshot older than this describes a sky the device has since left. */
const SNAPSHOT_STALE_MS = 10_000;

export type GnssTagValue = 'yes' | 'no' | 'unknown';

let snapshot: GnssSnapshot | null = null;
let unsubscribe: (() => void) | null = null;
let watchers = 0;

/**
 * Begin observing constellation status; returns the matching stop function.
 *
 * Reference counted, because more than one part of a round wants positioning at
 * once and the native callback fires about once a second — the last caller out
 * must be the one that turns it off.
 */
export const startGnssQualityWatch = (): (() => void) => {
  watchers += 1;
  if (watchers === 1 && !unsubscribe) {
    // Seed from whatever the native side already had, so the first fix of a hole
    // is tagged rather than being the one sample that reads 'unknown'.
    snapshot = getGnssSnapshot();
    unsubscribe = subscribeGnssStatus((next) => {
      snapshot = next;
    });
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    watchers = Math.max(0, watchers - 1);
    if (watchers === 0) {
      unsubscribe?.();
      unsubscribe = null;
      snapshot = null;
    }
  };
};

/** The current snapshot, or null when unavailable or too old to describe now. */
export const currentGnssSnapshot = (now: number = Date.now()): GnssSnapshot | null => {
  if (!snapshot) return null;
  return now - snapshot.at <= SNAPSHOT_STALE_MS ? snapshot : null;
};

/**
 * Metric tags describing the constellation behind a fix. Bounded to three values
 * per key by design — this rides on every accuracy sample, and an unbounded tag
 * is how a metrics bill becomes a metrics incident.
 */
export const gnssTags = (now: number = Date.now()): { sbas: GnssTagValue; l5: GnssTagValue } => {
  const current = currentGnssSnapshot(now);
  if (!current) return { sbas: 'unknown', l5: 'unknown' };
  return {
    sbas: current.sbasInUse ? 'yes' : 'no',
    // Null means the OS cannot report carrier frequency (below Android 8), which
    // is genuinely unknown rather than an absence of L5.
    l5: current.l5InUse === null ? 'unknown' : current.l5InUse ? 'yes' : 'no',
  };
};

/**
 * Report one positioned fix. Called from the hole view, which is both the place
 * accuracy is quoted to the player and a representative stream of ordinary
 * play — a fix a second while a player looks at a hole.
 */
export const reportFixQuality = ({
  accuracyMeters,
  sampleCount,
  now = Date.now(),
}: {
  accuracyMeters: number | null;
  sampleCount: number;
  now?: number;
}): void => {
  const tags = gnssTags(now);
  const withAveraging: MetricTags = {
    ...tags,
    averaged: sampleCount > 1 ? 'yes' : 'no',
  };

  if (accuracyMeters !== null && Number.isFinite(accuracyMeters) && accuracyMeters > 0) {
    recordHistogram('goffy.frontend.gnss.fix.accuracy', accuracyMeters, withAveraging);
  }

  const current = currentGnssSnapshot(now);
  if (current) {
    recordHistogram('goffy.frontend.gnss.satellites.used', current.satellitesUsed, tags);
  } else {
    // Counted, not silent: a fleet that is mostly 'unavailable' means the module
    // never shipped or the permission is being refused, and either one would
    // otherwise look like a quiet, healthy metric.
    incrementCounter('goffy.frontend.gnss.snapshot.missing', 1, {
      reason: isGnssStatusAvailable() ? 'stale' : 'unavailable',
    });
  }
};

export const __resetGnssQualityForTests = (): void => {
  unsubscribe?.();
  unsubscribe = null;
  snapshot = null;
  watchers = 0;
};
